import { useState } from 'react';
import type { DrCheckProbe, DrCheckReport } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useDrCheck } from '../api/drCheck.ts';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';

/**
 * `/dr-check` — DR readiness drill surface. Epic #109 P3.19.
 *
 * Manual "Run drill" button triggers the backend aggregator; result
 * renders as a per-probe table + overall pass/fail banner. No history
 * table persisted in phase 1 — ops runs the drill on demand and
 * archives the output externally if needed.
 */

export function DrCheckPage(): JSX.Element {
  const t = useT();
  const mutation = useDrCheck();
  const toast = useToast();
  const [lastReport, setLastReport] = useState<DrCheckReport | null>(null);

  async function handleRun(): Promise<void> {
    try {
      const report = await mutation.mutateAsync();
      setLastReport(report);
      if (report.overall === 'pass') {
        toast.success(t('dr_check.toast.pass'));
      } else {
        toast.error(t('dr_check.toast.fail'));
      }
    } catch (err) {
      const m =
        err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      toast.error(m);
    }
  }

  return (
    <section className="flex flex-col gap-8" data-testid="dr-check-page">
      <header className="flex flex-col gap-3">
        <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
          {t('dr_check.title')}
        </h2>
        <p className="text-sm text-on-surface-variant">{t('dr_check.subtitle')}</p>
      </header>

      <article
        data-testid="dr-check-runner-card"
        className="flex flex-col gap-4 rounded-xl bg-surface-container p-8"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-col gap-2">
            <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
              {t('dr_check.runner.title')}
            </h3>
            <p className="text-sm text-outline">{t('dr_check.runner.body')}</p>
          </div>
          <button
            type="button"
            onClick={() => void handleRun()}
            disabled={mutation.isPending}
            data-testid="dr-check-run"
            className="inline-flex items-center justify-center rounded-lg bg-tertiary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-tertiary-container transition-all hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
          >
            {mutation.isPending ? t('dr_check.action.running') : t('dr_check.action.run')}
          </button>
        </div>
        {lastReport === null ? (
          <p className="text-xs text-outline" data-testid="dr-check-idle">
            {t('dr_check.idle')}
          </p>
        ) : (
          <ReportBlock report={lastReport} />
        )}
      </article>
    </section>
  );
}

interface ReportBlockProps {
  readonly report: DrCheckReport;
}

function ReportBlock({ report }: ReportBlockProps): JSX.Element {
  const t = useT();
  const overallPass = report.overall === 'pass';
  return (
    <div className="flex flex-col gap-4">
      <div
        className={
          overallPass
            ? 'rounded-lg bg-surface-container-low p-4 text-sm text-on-surface'
            : 'rounded-lg bg-tertiary-container p-4 text-sm text-on-tertiary-container'
        }
        role={overallPass ? 'status' : 'alert'}
        data-testid="dr-check-overall-banner"
        data-outcome={report.overall}
      >
        <strong className="font-semibold">
          {overallPass
            ? t('dr_check.overall.pass_title')
            : t('dr_check.overall.fail_title')}
        </strong>{' '}
        {t('dr_check.overall.body', {
          started: report.started_at,
          duration: String(report.duration_ms),
        })}
      </div>
      <div className="overflow-x-auto">
        <table
          className="w-full text-left text-xs"
          data-testid="dr-check-probes-table"
        >
          <thead>
            <tr className="border-b border-outline-variant text-[10px] uppercase tracking-[0.08em] text-outline">
              <th scope="col" className="py-2 pr-4 font-semibold">
                {t('dr_check.column.probe')}
              </th>
              <th scope="col" className="py-2 pr-4 font-semibold">
                {t('dr_check.column.outcome')}
              </th>
              <th scope="col" className="py-2 pr-4 text-right font-semibold">
                {t('dr_check.column.latency')}
              </th>
              <th scope="col" className="py-2 pr-4 font-semibold">
                {t('dr_check.column.detail')}
              </th>
            </tr>
          </thead>
          <tbody>
            {report.probes.map((p) => (
              <ProbeRow key={p.name} probe={p} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface ProbeRowProps {
  readonly probe: DrCheckProbe;
}

function ProbeRow({ probe }: ProbeRowProps): JSX.Element {
  const t = useT();
  const isPass = probe.outcome === 'pass';
  return (
    <tr
      className="border-b border-outline-variant/40"
      data-testid={`dr-check-row-${probe.name}`}
      data-outcome={probe.outcome}
    >
      <td className="py-2 pr-4 font-mono text-[11px] text-on-surface">{probe.name}</td>
      <td className="py-2 pr-4">
        <span
          className={
            isPass
              ? 'inline-flex items-center rounded-full bg-surface-container-highest px-2 py-0.5 text-[10px] font-bold uppercase text-on-surface'
              : 'inline-flex items-center rounded-full bg-tertiary-container px-2 py-0.5 text-[10px] font-bold uppercase text-on-tertiary-container'
          }
        >
          {isPass ? t('dr_check.probe.pass') : t('dr_check.probe.fail')}
        </span>
      </td>
      <td className="py-2 pr-4 text-right font-mono text-[11px] text-on-surface">
        {probe.latency_ms}ms
      </td>
      <td className="py-2 pr-4">
        {probe.error !== null ? (
          <p className="font-mono text-[11px] text-error">{probe.error}</p>
        ) : probe.detail !== null ? (
          <pre className="whitespace-pre-wrap font-mono text-[10px] text-outline">
            {JSON.stringify(probe.detail, null, 2)}
          </pre>
        ) : (
          <span className="text-[11px] text-outline">—</span>
        )}
      </td>
    </tr>
  );
}
